<template>
  <ui-portal
    class="ui-modal"
    v-if="isOpenInStore"
    :to="portalTarget"
    :disabled="!appendToBody"
  >
    <transition name="fade" mode="out-in" appear>
      <div
        class="ui-modal__container"
        :class="classList"
        ref="modalContainer"
        data-fl-body-scroll-lock-ignore="1"
        v-bind="vDeepAttributes"
      >
        <div
          class="ui-modal__overlay"
          ref="overlay"
          tabindex="-1"
          @click.self="onAwayClick"
          @keyup.enter.stop="onKeyupEnter"
          @keyup.esc.stop="onKeyupEsc"
          @blur.stop="onBlur"
        >
          <div
            class="ui-modal__window"
            :style="windowStyle"
          >
            <ui-loading
              v-if="loading"
              type="absolute"
              is-open
              hide-transition
            />
            <div class="ui-modal__header">
              <div
                class="ui-modal__header-info"
                v-if="hasHeader"
              >
                <ui-button
                  class="ui-modal__back"
                  v-if="hasBackButton"
                  title="Назад"
                  icon="uiChevronLeft"
                  button-style="link"
                  size="s"
                  @click="close"
                />
                <div class="ui-modal__title">
                  <template v-if="title">
                    {{title}}
                  </template>
                </div>
                <div
                  v-if="hasDescription"
                  class="ui-modal__description"
                >
                  <slot name="description">
                    {{ description }}
                  </slot>
                </div>
              </div>
              <div
                class="ui-modal__close"
                v-if="hasCloseIcon"
                @click="close"
              >
                <ui-icon
                  :size="32"
                  name="uiClose"
                />
              </div>
            </div>
            <div class="ui-modal__body">
              <slot />
            </div>
            <div class="ui-modal__footer" v-if="hasFooter">
              <slot name="footerStart" />
              <slot name="footer">
                <ui-button
                  class="ui-modal__button _cancel"
                  v-if="hasCancelButton"
                  :title="cancelButtonTitle"
                  type="secondary"
                  size="l"
                  @click="close"
                />
                <ui-button
                  class="ui-modal__button _success"
                  v-if="hasSuccessButton"
                  :title="successButtonTitle"
                  size="l"
                  @click="success"
                />
              </slot>
              <slot name="footerEnd" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </ui-portal>
</template>

<script>
import { UiButton, UiIcon } from '../../components'
import UiLoading from '../loader/UiLoading.vue'
import UiPortal from '../portal/UiPortal.vue'

import vDeepMixin from '../../mixins/vDeepMixin'

import { useModalsStore } from '../../stores/modals'
import { POSITIONS } from '../../libs/helpConsts'

const SCROLL_TYPES = {
  INNER: 'inner',
  OUTER: 'outer'
}

export default {
  name: 'UiModal',
  components: {
    UiButton,
    UiPortal,
    UiLoading,
    UiIcon
  },
  mixins: [vDeepMixin],
  props: {
    isOpen: Boolean,
    appendToBody: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'm',
      validator (size) {
        return [
          's',
          'm',
          'l',
          'xl',
          'auto',
          'full-height',
          'full-page'
        ].includes(size)
      }
    },
    canHiddenFromChild: {
      type: Boolean,
      default: true
    },
    canHideParent: {
      type: Boolean,
      default: true
    },
    modalKey: String,
    title: String,
    description: String,
    portalTarget: {
      type: String,
      default: 'modals'
    },
    hasCancelButton: {
      type: Boolean,
      default: true
    },
    hasSuccessButton: {
      type: Boolean,
      default: true
    },
    cancelButtonTitle: {
      type: String,
      default: 'Отменить'
    },
    successButtonTitle: {
      type: String,
      default: 'Сохранить'
    },
    isClickAwayClose: {
      type: Boolean,
      default: false
    },
    hasOverlay: {
      type: Boolean,
      default: true
    },
    isContentCenter: Boolean,
    loading: Boolean,
    hasCloseIcon: {
      type: Boolean,
      default: true
    },
    hasHeader: {
      type: Boolean,
      default: true
    },
    hasFooter: {
      type: Boolean,
      default: true
    },
    isFooterCenter: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: POSITIONS.CENTER,
      validator (position) {
        return [POSITIONS.CENTER, POSITIONS.TOP].indexOf(position) !== -1
      }
    },
    scrollType: {
      type: String,
      default: SCROLL_TYPES.OUTER,
      validator (scrollType) {
        return Object.values(SCROLL_TYPES).includes(scrollType)
      }
    },
    titleCenter: Boolean,
    hasBackButton: Boolean,
    windowPosition: Object,
    isCustomClose: Boolean
  },
  data () {
    return {
      modalsStore: useModalsStore(),
      containerClassList: []
    }
  },
  computed: {
    innerModalKey () {
      return this.modalKey ?? this
    },
    isOpenInStore: {
      get () {
        const modalSetFieldName = this.canHideParent ? 'openedModalsSet' : 'openedOuterModalsSet'
        return this.modalsStore[modalSetFieldName].has(this.innerModalKey)
      },
      set (isOpen) {
        this.updateIsOpen(isOpen)
      }
    },
    isActiveModal () {
      const lastModalFieldName = this.canHideParent ? 'lastModal' : 'lastOuterModal'
      return this.modalsStore[lastModalFieldName] === this.innerModalKey
    },
    isHiddenModal () {
      if (!this.canHiddenFromChild) return false
      return !this.isActiveModal
    },
    classList () {
      return [
        ...this.vDeepClassList,
        `_position_${this.position}`,
        `_size_${this.size}`,
        `_scroll_${this.scrollType}`,
        {
          _hidden: this.isHiddenModal,
          _pointer: this.isClickAwayClose && this.hasOverlay,
          '_hidden-overlay': !this.hasOverlay,
          _content_center: this.isContentCenter,
          _footer_center: this.isFooterCenter,
          _title_center: this.titleCenter
        }
      ]
    },
    windowStyle () {
      if (!this.windowPosition || !Object.keys(this.windowPosition).length) return
      const { top, right, left, bottom } = this.windowPosition
      return { top, right, left, bottom }
    },
    hasDescription () {
      return Boolean(this.$slots.description || this.description)
    }
  },
  watch: {
    isOpen: {
      handler (isOpen) {
        if (isOpen) this.open()
        else this.close()
      },
      immediate: true
    },
    appendToBody (isAppendToBody) {
      if (this.isOpen) {
        // При перемещении модального окна перестаёт работать v-deep, т.к. он основывается на родительских классах и scopeId
        // Для восстановления v-deep переносим в этот компонент классы родителя и корневого элемента модального окна
        // Затем переносим родительский _scopeId
        this.initVDeep()
      }
      if (!isAppendToBody) this.clearVDeep()
    },
    isActiveModal (isActiveModal) {
      if (!isActiveModal) return
      this.focus()
    },
    'modalsStore.lastOuterModal' () {
      if (!this.isActiveModal) return
      this.focus()
    },
    isOpenInStore (isOpenInStore) {
      if (this.isOpen !== isOpenInStore) this.updateIsOpen(isOpenInStore)
      if (isOpenInStore) {
        this.$nextTick(() => this.modalsStore.disableBodyScroll(this.$refs.modalContainer))
      } else {
        this.modalsStore.enableBodyScroll(this.$refs.modalContainer)
      }
    }
  },
  beforeDestroy () {
    if (!this.isOpen) return
    this.modalsStore.enableBodyScroll(this.$refs.modalContainer)
    this.modalsStore.closeModal(this.innerModalKey, !this.canHideParent)
  },
  methods: {
    open () {
      if (this.isOpenInStore) return
      if (!this.isOpen) this.isOpenInStore = true

      this.$nextTick(() => {
        if (this.appendToBody) {
          this.initVDeep()
        }
        this.focus()
      })

      this.modalsStore.openModal(this.innerModalKey, !this.canHideParent)
    },
    close () {
      if (!this.isOpenInStore) return
      if (this.isCustomClose && this.isOpen) {
        this.updateIsOpen(false)
        return
      }
      if (this.isOpen) this.isOpenInStore = false

      this.modalsStore.closeModal(this.innerModalKey, !this.canHideParent)
    },
    updateIsOpen (isOpen) {
      this.$emit('update:is-open', isOpen)
      this.$emit(isOpen ? 'open' : 'close')
    },
    onAwayClick () {
      if (!this.isClickAwayClose) return
      this.close()
    },
    success () {
      this.$emit('success')
    },
    focus () {
      this.$refs.overlay?.focus?.()
    },
    onKeyupEnter () {
      this.success()
    },
    onKeyupEsc () {
      this.close()
    },
    onBlur () {
      if (!this.isActiveModal || this.modalsStore.lastOuterModal !== this.innerModalKey) return
      setTimeout(this.focus, 0)
    }
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
  @include transition-list(opacity);
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.ui-modal {
  $parent: &;
  position: fixed;
  height: 0;
  width: 0;
  overflow: hidden;

  &__overlay {
    @include flex-center;
    @include transition-list(opacity);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1001;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
    padding: 3vh 3vw;
    background: rgba($black, 0.2);
    border: none;
    outline: none;
  }

  &__window {
    @include flex-column;
    @include hidden-scrollbar;
    position: relative;
    max-width: 100%;
    padding: 24px;
    background: $white;
    border-radius: $border-radius-00;
    box-shadow: 0 2px 7px rgba($black, 0.25);
    cursor: auto;
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 12px;
  }

  .ui-modal__container &__back {
    justify-self: flex-start;
    padding: 0;
  }

  &__body {
    @include flex-column;
    flex-grow: 1;
  }

  &__header-info {
    @include grid;
    grid-gap: 2px;
    flex-grow: 1;
    margin-bottom: 24px
  }

  &__header-content {
    @include grid;
    grid-gap: 2px;
  }

  &__title {
    @include font-title-22;
    color: $gray-1;
  }

  &__close {
    @include flex-center;
    @include transition-list(fill);
    fill: $gray-1;
    cursor: pointer;

    :hover {
      fill: $red-1;
    }
  }

  &__description {
    font-size: 14px;
    line-height: 20px;
    color: $gray-3;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px 16px;
    flex-wrap: wrap-reverse;
    margin-top: 30px;
  }

  ::v-deep() &__button {
    &._success {
      min-width: 200px;
    }
  }

  &__container {
    z-index: 1;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;

    &._pointer {
      #{$parent}__overlay {
        cursor: pointer;
      }
    }

    &._hidden {
      #{$parent}__overlay {
        opacity: 0;
      }
    }

    &._hidden-overlay {
      #{$parent}__overlay {
        pointer-events: all;
        background: transparent;
      }

      #{$parent}-window {
        pointer-events: unset;
      }
    }

    &._content_center {
      #{$parent}__body {
        @include flex-center;
      }
    }

    &._footer_center {
      #{$parent}__footer {
        @include flex-center;
      }
    }

    &._position {
      &_top {
        #{$parent}__overlay {
          align-items: flex-start;
        }
      }
    }

    &._title_center {
      #{$parent}__header {
        $closeIconSize: 32px;
        $gap: 12px;
        position: relative;
        min-height: $closeIconSize;
        padding: 0 calc($closeIconSize + $gap);
      }

      #{$parent}__close {
        position: absolute;
        right: 0;
      }

      #{$parent}__title,
      #{$parent}__description {
        @include flex-center;
        position: relative;
      }
    }

    &._size {
      &_s {
        #{$parent}__window {
          width: 550px;
        }
      }

      &_m {
        #{$parent}__window {
          width: 680px;
        }
      }

      &_l {
        #{$parent}__window {
          width: 100%;
          max-width: 1002px;
        }
      }

      &_xl {
        #{$parent}__window {
          width: 100%;
          max-width: 1140px;
        }
      }

      &_auto {
        #{$parent}__window {
          max-width: 100%;
        }
      }

      &_m {
        #{$parent}__window {
          width: 680px;
        }
      }

      &_full-height,
      &_full-page {
        #{$parent}__window {
          @include flex-column;
          min-height: 100%;
          overflow-y: auto;
        }
      }

      &_full-page {
        #{$parent}__window {
          width: 100%;
        }
      }
    }

    &._scroll {
      &_outer {
        #{$parent}__overlay {
          overflow-x: auto;
        }
      }

      &_inner {
        #{$parent}__window {
          max-height: 100%;
        }

        #{$parent}__body {
          overflow-y: auto;
        }
      }
    }
  }

  @include media-max-wide-sm {
    &__title {
      font-size: 20px;
    }

    ::v-deep() &__button {
      width: 100%;

      &._success {
        min-width: unset;
      }
    }

    &__container {
      &._size {
        &_full-page {
          #{$parent}__window {
            width: 100%;
            border-radius: 0;
          }

          #{$parent}__overlay {
            padding: 0;
          }
        }
      }
    }
  }
}
</style>
